/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState, useCallback, useEffect } from 'react';

export const useResizable = (initialSize = { width: 300, height: 200 }, constraints, position = { x: 0, y: 0 }) => {
  const [size, setSize] = useState(() => {
    if (!constraints) {
      return initialSize;
    }

    return {
      width: Math.min(Math.max(initialSize.width, constraints.minWidth), constraints.maxWidth),
      height: Math.min(Math.max(initialSize.height, constraints.minHeight), constraints.maxHeight)
    };
  });
  const [isResizing, setIsResizing] = useState(false);

  const getConstrainedSize = useCallback(
    (sizeToConstrain) => {
      const maxWidth = Math.min(constraints?.maxWidth ?? Infinity, window.innerWidth - position.x);
      const maxHeight = Math.min(constraints?.maxHeight ?? Infinity, window.innerHeight - position.y);
      const minWidth = constraints?.minWidth ?? 0;
      const minHeight = constraints?.minHeight ?? 0;

      const newWidth = Math.min(Math.max(sizeToConstrain.width, minWidth), maxWidth);
      const newHeight = Math.min(Math.max(sizeToConstrain.height, minHeight), maxHeight);

      return { width: newWidth, height: newHeight };
    },
    [constraints, position]
  );

  useEffect(() => {
    const handleWindowResize = () => {
      setSize((currentSize) => {
        const newSize = getConstrainedSize(currentSize);

        if (newSize.width === currentSize.width && newSize.height === currentSize.height) {
          return currentSize;
        }

        return newSize;
      });
    };

    window.addEventListener('resize', handleWindowResize);

    handleWindowResize();

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [getConstrainedSize]);

  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = size.width;
      const startHeight = size.height;

      const handleResize = (e) => {
        const newWidth = startWidth + (e.clientX - startX);
        const newHeight = startHeight + (e.clientY - startY);

        setSize(getConstrainedSize({ width: newWidth, height: newHeight }));
      };

      const handleResizeEnd = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleResize);
        document.removeEventListener('mouseup', handleResizeEnd);
      };

      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', handleResizeEnd);
    },
    [size, getConstrainedSize]
  );

  return { size, isResizing, handleResizeStart };
};
